import React, { useContext, useEffect, useState, useRef } from "react";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { Button, FormControl, FormHelperText, IconButton, Image, Input, InputGroup, InputLeftElement, InputRightElement, Text, useToast } from "@chakra-ui/react";
import { login, loginOTP, userLogin, sendOTPForUserLogin } from "../../APIs";
import { hidePwdIcon as HidePassword, showPwdIcon as ShowPassword } from "../../Assets/images/svgs";
import { isValidMobile } from "../../Library/validations";
import { AuthContext } from "../../Library/contexts";
import PATHS from "../../Routes/paths";
import { LOGIN_REDIRECT_PATHS } from "../../Library/constants";
import { getItemFromLocalStorage } from "../../Library/helpers";

const LoginPage = () => {
  const toast = useToast();

  const { assets, setProfile } = useContext(AuthContext);
  const isUser = useRef(getItemFromLocalStorage());

  const [showPassword, setShowPassword] = useState(false);
  const [disableOtp, setDisableOtp] = useState(false);
  const [otpLogin, setOTPLogin] = useState(false);

  const [loginCredentials, setLoginCredentials] = useState({});
  const [isError, setIsError] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Clear any persisted driver session so the PIN is always required after a fresh login
    localStorage.removeItem('driverInfo');
    localStorage.removeItem('driverPinEnteredAt');

    if (!isUser.current) {
      window.localStorage.setItem("theme", "light");
      document.body.classList.add("Ev-Light-Theme-Container");
    } else {
      window.localStorage.removeItem("theme");
      document.body.classList.remove("Ev-Light-Theme-Container");
    }
  }, [])

  const sendOTP = () => {
    if (!isValidMobile(loginCredentials.mobile)) {
      setIsError({ invalidMobile: "Invalid Mobile Number" });
    } else {
      const body = { mob: Number(loginCredentials.mobile), sendOtp: true };

      var loginFunc = isUser.current ? sendOTPForUserLogin : login;

      loginFunc(body, (failure) => {
        if (failure) setIsError({ invalidOTP: failure });
        else {
          setDisableOtp(true);
          toast({ title: "OTP Sent Successfully", variant: "subtle", position: "bottom-right" });
        }
      });
    }
  };

  const getLoginCredentials = (e) => {
    setLoginCredentials({
      ...loginCredentials,
      [e.target.name]: e.target.value,
    });
  };

  const validateLoginCredentials = () => {
    if (!isValidMobile(loginCredentials.mobile)) {
      setIsError({ invalidMobile: "Invalid Mobile Number" });
    } else if (!otpLogin && !loginCredentials.password) {
      setIsError({ invalidPassword: "Invalid Password" });
    } else if (otpLogin && !loginCredentials.otp) {
      setIsError({ invalidOTP: "Invalid OTP" });
    } else return true;

    return false;
  };

  const validateAndLogin = (e) => {
    e.preventDefault();

    if(validateLoginCredentials()) {
      const body = {};

      if (isUser.current) {
        body.cmob = "+91" + loginCredentials.mobile;
        if (otpLogin) body.otp = loginCredentials.otp;
        else body.pwd = loginCredentials.password;
        body.isWeb = true;
      } else {
        body.mob = Number(loginCredentials.mobile);
        if (otpLogin) body.otp = loginCredentials.otp;
        else body.pwd = loginCredentials.password;
      }

      setLoading(true);

    const loginFunction = isUser.current ? userLogin : (otpLogin ? loginOTP : login);
      const errorKey = otpLogin ? "invalidOTP" : "invalidPassword";

      loginFunction(body, (failure, success) => {
        setLoading(false);
        let navigateTo = "";
        // let fakeError = "Unable to Login! Please Contact Ajjas.";
        // setIsError({ invalidPassword: fakeError, invalidOTP: fakeError });

        if (failure) setIsError({ [errorKey]: failure });
        else {
          if (success && success.profile) {
            if (success.profile.accessCodes) window.localStorage.setItem("accessCodes", JSON.stringify(success.profile.accessCodes));
            if (success.profile.des) window.localStorage.setItem("des", success.profile.des);
            if (success.profile.rly) window.localStorage.setItem("relay", success.profile.rly);
            else window.localStorage.removeItem("relay");

            if (success.profile.rlyCntrl) window.localStorage.setItem("relayControl", success.profile.rlyCntrl);
            else window.localStorage.removeItem("relayControl");

            if (success.profile.accessCodes && success.profile.accessCodes.length) navigateTo = LOGIN_REDIRECT_PATHS[success.profile.accessCodes[0]];
            else navigateTo = PATHS.LEASE;

            if (success.profile.user) window.localStorage.setItem("user", true);
          }

          setProfile(success.profile);
          navigate(navigateTo);
        }
      });
    };
  }

  return (
    <div className="login-page">
      <Image src={assets.assetUrl + "/" + assets.bg} alt="" className="page-background-image" />
      <div className="login-form-container">
        <Image src={assets.assetUrl + "/" + assets.brandImg} className="drawer-logo" />
        <form className="login-form">
          <FormControl isInvalid={isError.invalidMobile} className="login-form-control" onSubmit={validateAndLogin}>
            <Text ml="10px" className="mobile-input-label">
              Mobile
            </Text>
            <InputGroup className="mobile-input-group">
              <InputLeftElement
                pointerEvents="none"
                children="+91"
                color="white"
                className="input-add-on"
                ml="15px"
              />
              <Input
                errorBorderColor="#DF5050"
                onChange={getLoginCredentials}
                name="mobile"
                pattern="^[0-9]{10}$"
                type="tel"
                variant="flushed"
                color="white"
                className="form-mobile-number"
              />
              {otpLogin && (
                <InputRightElement>
                  <Button variant="link" className="get-otp-button" onClick={sendOTP} isDisabled={disableOtp}>
                    Get OTP
                  </Button>
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>

          <FormControl
            isInvalid={isError.invalidPassword || isError.invalidOTP}
            className="login-form-control"
          >
            <Text ml="10px" className="password-input-label">
              {otpLogin ? "OTP" : "Password"}
            </Text>
            <InputGroup className="password-input-group">
              <Input
                errorBorderColor="#DF5050"
                onChange={getLoginCredentials}
                name={otpLogin ? "otp" : "password"}
                type={showPassword ? "text" : "password"}
                variant="flushed"
                color="white"
                className="form-password"
              />
              <InputRightElement>
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  colorScheme="transparent"
                  icon={showPassword ? <HidePassword /> : <ShowPassword />}
                />
              </InputRightElement>
            </InputGroup>

            <FormHelperText className="error-message">
              { isError.invalidMobile || isError.invalidPassword || isError.invalidOTP || "" }
            </FormHelperText>
            <Button onClick={validateAndLogin} type="submit" className="form-submit-button" loadingText="Login" isLoading={loading}>
              Login
            </Button>
            <Button
              variant="link"
              className="otp-login-button"
              onClick={() => setOTPLogin(!otpLogin)}
            >
              Login with {otpLogin ? "Password" : "OTP"}
            </Button>
          </FormControl>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;